import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { dateTimeEs } from "@/lib/formatters";
import {
  Inbox, Mail, Phone, MessageCircle, UserPlus, ArrowRight, Trash2, Loader2, Search,
  Building2, Filter,
} from "lucide-react";

const STATUS_META = {
  nuevo: { label: "Nuevo", color: "bg-blue-50 text-blue-700 border-blue-300" },
  contactado: { label: "Contactado", color: "bg-amber-50 text-amber-700 border-amber-300" },
  convertido: { label: "Convertido", color: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  descartado: { label: "Descartado", color: "bg-slate-100 text-slate-600 border-slate-300" },
};

export default function ProspectsList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [converting, setConverting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      const r = await api.get("/prospects", { params });
      setItems(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const filtered = q
    ? items.filter((p) => `${p.name} ${p.company || ""} ${p.email || ""} ${p.phone || ""}`.toLowerCase().includes(q.toLowerCase()))
    : items;

  const changeStatus = async (id, status) => {
    try {
      const r = await api.patch(`/prospects/${id}`, { status });
      setItems((prev) => prev.map((p) => p.id === id ? r.data : p));
      if (selected?.id === id) setSelected(r.data);
      toast.success("Estado actualizado");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const saveNotes = async (id, notes) => {
    try {
      const r = await api.patch(`/prospects/${id}`, { notes });
      setItems((prev) => prev.map((p) => p.id === id ? r.data : p));
      setSelected(r.data);
      toast.success("Notas guardadas");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const convertToClient = async (p) => {
    if (!window.confirm(`¿Convertir a ${p.name} en cliente? Se creará un registro en Clientes.`)) return;
    setConverting(true);
    try {
      const r = await api.post(`/prospects/${p.id}/convert-to-client`);
      toast.success("Cliente creado");
      // Refresh list; take user to new quote pre-filled
      await load();
      setSelected(null);
      nav(`/quotes/new?client=${r.data.client_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setConverting(false); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este prospecto?")) return;
    try {
      await api.delete(`/prospects/${id}`);
      setItems((prev) => prev.filter((p) => p.id !== id));
      setSelected(null);
      toast.success("Eliminado");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const isAdmin = user?.role === "admin";

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6" data-testid="prospects-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-black tracking-[0.24em] uppercase text-[#005A91]">Módulo</div>
          <h1 className="text-3xl lg:text-4xl font-black text-[#0A1118] tracking-tight">Prospectos</h1>
          <p className="text-slate-500 text-sm mt-1">Bandeja de leads capturados desde la landing pública</p>
        </div>
      </div>

      <div className="pe-card p-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="prospects-search" placeholder="Buscar nombre, empresa, correo…" value={q} onChange={(e) => setQ(e.target.value)} className="h-11 pl-9 border-[#005A91]/20" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="prospects-status-filter" className="h-11 w-44 border-[#005A91]/20"><Filter className="h-4 w-4 mr-1" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="nuevo">Nuevos</SelectItem>
            <SelectItem value="contactado">Contactados</SelectItem>
            <SelectItem value="convertido">Convertidos</SelectItem>
            <SelectItem value="descartado">Descartados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="pe-card overflow-x-auto">
        {loading ? (
          <div className="text-center py-10 text-slate-500">Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-500" data-testid="prospects-empty">
            <Inbox className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <div className="font-semibold">Sin prospectos aún</div>
            <div className="text-sm mt-1">Cuando alguien envíe una solicitud desde la landing, aparecerá aquí.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-black uppercase tracking-wider text-slate-500 border-b border-[#005A91]/10">
                <th className="py-2.5 pr-3">Nombre / Empresa</th>
                <th className="py-2.5 pr-3">Contacto</th>
                <th className="py-2.5 pr-3">Servicio</th>
                <th className="py-2.5 pr-3">Estado</th>
                <th className="py-2.5 pr-3">Recibido</th>
                <th className="py-2.5 pr-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const s = STATUS_META[p.status] || STATUS_META.nuevo;
                return (
                  <tr key={p.id} data-testid={`prospect-row-${p.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 pr-3">
                      <button onClick={() => setSelected(p)} className="text-left" data-testid={`open-prospect-${p.id}`}>
                        <div className="font-semibold text-[#0A1118] hover:text-[#005A91]">{p.name}</div>
                        {p.company && <div className="text-xs text-slate-500 flex items-center gap-1"><Building2 className="h-3 w-3" /> {p.company}</div>}
                      </button>
                    </td>
                    <td className="py-3 pr-3 text-slate-600">
                      {p.email && <div className="flex items-center gap-1 text-xs"><Mail className="h-3 w-3" /> {p.email}</div>}
                      {p.phone && <div className="flex items-center gap-1 text-xs mt-0.5"><Phone className="h-3 w-3" /> {p.phone}</div>}
                    </td>
                    <td className="py-3 pr-3 text-slate-600 text-xs">{p.service_interest || "—"}</td>
                    <td className="py-3 pr-3">
                      <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${s.color}`}>{s.label}</span>
                    </td>
                    <td className="py-3 pr-3 text-slate-500 text-xs">{dateTimeEs(p.created_at)}</td>
                    <td className="py-3 pr-3 text-right">
                      <div className="flex justify-end gap-1">
                        {p.status !== "convertido" && (
                          <Button size="sm" onClick={() => convertToClient(p)} data-testid={`convert-btn-${p.id}`} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
                            <UserPlus className="h-3 w-3 mr-1" /> Convertir
                          </Button>
                        )}
                        {p.status === "convertido" && p.converted_client_id && (
                          <Button size="sm" variant="outline" onClick={() => nav(`/clients?highlight=${p.converted_client_id}`)} data-testid={`view-client-${p.id}`} className="h-8 border-emerald-300 text-emerald-700 text-xs">
                            Ver cliente <ArrowRight className="h-3 w-3 ml-1" />
                          </Button>
                        )}
                        {isAdmin && (
                          <Button size="sm" variant="ghost" onClick={() => del(p.id)} data-testid={`delete-prospect-${p.id}`} className="h-8 w-8 p-0 hover:bg-red-50">
                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-[#005A91]">{selected.name}</DialogTitle>
                <DialogDescription>Recibido {dateTimeEs(selected.created_at)} — IP {selected.ip}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><b>Empresa:</b> {selected.company || "—"}</div>
                  <div><b>Servicio:</b> {selected.service_interest || "—"}</div>
                  <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-[#005A91]" /> {selected.email ? <a href={`mailto:${selected.email}`} className="text-[#005A91] hover:underline">{selected.email}</a> : "—"}</div>
                  <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-[#005A91]" /> {selected.phone ? <a href={`tel:${selected.phone}`} className="text-[#005A91] hover:underline">{selected.phone}</a> : "—"}</div>
                </div>
                {selected.message && (
                  <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm">
                    <div className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Mensaje</div>
                    <div className="whitespace-pre-wrap">{selected.message}</div>
                  </div>
                )}
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Estado</Label>
                  <Select value={selected.status} onValueChange={(v) => changeStatus(selected.id, v)}>
                    <SelectTrigger data-testid="detail-status" className="mt-1 h-11 border-[#005A91]/20"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nuevo">Nuevo</SelectItem>
                      <SelectItem value="contactado">Contactado</SelectItem>
                      <SelectItem value="convertido">Convertido</SelectItem>
                      <SelectItem value="descartado">Descartado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Notas internas</Label>
                  <Textarea data-testid="detail-notes" rows={3} defaultValue={selected.notes || ""} onBlur={(e) => e.target.value !== (selected.notes || "") && saveNotes(selected.id, e.target.value)} className="mt-1 border-[#005A91]/20 focus-visible:ring-[#4ECCC8]" />
                </div>
                {selected.email && (
                  <a href={`mailto:${selected.email}?subject=${encodeURIComponent("Power & Energy — su solicitud")}&body=${encodeURIComponent("Estimado(a) " + selected.name + ",\n\nGracias por contactarnos...\n")}`}
                      className="inline-flex items-center gap-2 text-sm text-[#2E75B5] font-bold hover:underline">
                    <Mail className="h-4 w-4" /> Responder por correo
                  </a>
                )}
              </div>
              <DialogFooter>
                {selected.status !== "convertido" ? (
                  <Button onClick={() => convertToClient(selected)} disabled={converting} data-testid="detail-convert-btn" className="bg-emerald-600 hover:bg-emerald-700 font-bold">
                    {converting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                    Convertir en cliente y cotizar
                  </Button>
                ) : (
                  <Button onClick={() => nav(`/quotes/new?client=${selected.converted_client_id}`)} className="bg-[#2E75B5] hover:bg-[#3a88cd] font-bold" data-testid="detail-new-quote-btn">
                    Nueva cotización <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
